import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { FC, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AvailablePhysicianSlot,
  toWireReason,
  useAvailablePhysiciansWithSlotsV2,
  VisitReason,
} from '../AppointmentService'
import { format } from 'date-fns'
import { useGetPerson } from '~/entities/person'
import BookAppointmentByDayTab from './BookAppointmentByDayTab'
import BookAppointmentByProviderTab from './BookAppointmentByprovider'

type BookAppointmentTabTypes = 'byDay' | 'byProvider'

const DATE_STR_FORMAT = 'MMM d, yyyy'

type BookAppointmentProps = {
  patientId: number
  personId: number
  visitReason: VisitReason | null
  now: Date
  date: Date
  setDate: (value: Date) => void
  slot: AvailablePhysicianSlot | null
  setSlot: (value: AvailablePhysicianSlot | null) => void
  setIsLoading: (e: boolean) => void
  isLoading: boolean
  setIsCustomAppointment: (value: boolean) => void
  setCustomAppointmentReasons: (value: string[]) => void
}

interface TabPanelProps {
  children: ReactNode
  value: BookAppointmentTabTypes
  selectedTab: BookAppointmentTabTypes
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, selectedTab } = props
  const visible = value === selectedTab

  return (
    <div
      role="tabpanel"
      hidden={!visible}
      id={`book-appointment-tabpanel-${value}`}
      aria-labelledby={`book-appointment-tab-${value}`}
    >
      {visible && <Box>{children}</Box>}
    </div>
  )
}

const BookAppointment: FC<BookAppointmentProps> = ({
  patientId,
  personId,
  visitReason,
  date,
  setDate,
  now,
  slot,
  setSlot,
  setIsLoading,
  isLoading,
  setIsCustomAppointment,
  setCustomAppointmentReasons,
}) => {
  const { data: person } = useGetPerson({ personId: personId })
  const [tab, setTab] = useState<BookAppointmentTabTypes>('byDay')
  const isByDay = tab === 'byDay'
  // By Day reads the length from its own V2 slots query, gated so it does not fetch purely for the length
  // while By Provider is active. By Provider reports its length up from its own slots response, so the
  // displayed length always reflects the active tab (and no redundant By-Day fetch fires on By Provider).
  const { data: byDaySlotData } = useAvailablePhysiciansWithSlotsV2(
    patientId,
    visitReason,
    date,
    isByDay,
  )
  const [byProviderDurationMinutes, setByProviderDurationMinutes] = useState<number | null>(null)
  const durationMinutes = isByDay ? byDaySlotData?.duration_minutes : byProviderDurationMinutes
  const date_of_last_awv = person?.primary_care_program_info?.date_of_last_awv
  const date_of_last_awv_as_date = new Date(date_of_last_awv ?? '')
  const today = new Date()
  const isEstablishedAwv =
    visitReason != null && toWireReason(visitReason) === 'Annual Wellness Visit Established'
  const due_beginning =
    isEstablishedAwv && date_of_last_awv
      ? date_of_last_awv_as_date.setFullYear(date_of_last_awv_as_date.getFullYear() + 1)
      : null
  return (
    <>
      {visitReason ? (
        <Box sx={{ m: 2 }}>
          {durationMinutes != null ? (
            <Typography sx={{ color: 'navy.500', fontSize: 16, fontWeight: 400 }}>
              {'Appt length: '}
              {durationMinutes} {'min'}
            </Typography>
          ) : null}
          <Typography sx={{ color: 'navy.500', fontSize: 16, fontWeight: 400 }}>
            {(!date_of_last_awv && isEstablishedAwv) ||
            (due_beginning && new Date(due_beginning) < today)
              ? 'Overdue'
              : due_beginning
                ? `Due beginning: ${format(due_beginning, DATE_STR_FORMAT)}`
                : null}
          </Typography>
        </Box>
      ) : null}
      {visitReason ? (
        <Box sx={{ m: 2 }}>
          <>
            <ToggleButtonGroup
              color="primary"
              value={tab}
              exclusive
              onChange={(_, nextTab: BookAppointmentTabTypes | null) => {
                if (!nextTab) {
                  return
                }
                setTab(nextTab)
                setDate(now)
                setSlot(null)
                // Clear the By-Provider length; the tab remounts with no physician selected, so a stale
                // value from a previously-picked physician must not linger.
                setByProviderDurationMinutes(null)
              }}
              size="small"
              aria-label="Status"
              sx={{
                '.MuiToggleButton-root.MuiToggleButtonGroup-grouped': {
                  padding: '7px 20px',
                  textTransform: 'capitalize',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '.MuiToggleButton-root.Mui-selected': {
                  backgroundColor: 'tones.neutral.bg',
                  color: 'text.primary',
                },
              }}
            >
              <ToggleButton value="byDay">By Day</ToggleButton>
              <ToggleButton value="byProvider">By Provider</ToggleButton>
            </ToggleButtonGroup>
          </>
          <TabPanel value="byDay" selectedTab={tab}>
            <BookAppointmentByDayTab
              patientId={patientId}
              visitReason={visitReason}
              date={date}
              setDate={setDate}
              now={now}
              slot={slot}
              setSlot={setSlot}
              setIsLoading={setIsLoading}
            />
          </TabPanel>
          <TabPanel value="byProvider" selectedTab={tab}>
            <BookAppointmentByProviderTab
              patientId={patientId}
              visitReason={visitReason}
              date={date}
              setDate={setDate}
              now={now}
              slot={slot}
              setSlot={setSlot}
              setIsCustomAppointment={setIsCustomAppointment}
              setCustomAppointmentReasons={setCustomAppointmentReasons}
              setDurationMinutes={setByProviderDurationMinutes}
            />
          </TabPanel>
        </Box>
      ) : !isLoading ? (
        <Box sx={{ m: 2 }}>
          <Typography>{'Patient is not eligible to book appointment.'}</Typography>
        </Box>
      ) : null}
    </>
  )
}

export default BookAppointment
